import { calculatePercentage, getExperienceUrl, isStringEmpty } from '@/lib/commonHelpers';
import { useExperienceStore } from '@/store/experienceStore';
import { useTopicStore } from '@/store/topicStore';
import { useSegmentStore } from '@/store/useSegmentStore';
import { PlanetLinear } from '@assessprep/icons/solar';
import { Attachment01Line, Lock02Line, Shield03Line } from '@assessprep/icons/untitled';
import { useInvalidate } from '@refinedev/core';
import _ from 'lodash';
import { useRealtimeExperienceStore } from '../store/realtimeExperienceStore';

import { RubricTypes } from '@/components/ap/rubric/rubricTypes';
import { checkIfStartTime } from '@/lib/commonHelpers';
import {
  activeAIGradingRunForSession,
  useAIGradingRunsStore,
} from '@/pages/experiences/show/manage/grade/ai/aiGradingRunsStore';
import { useUserResponseStore } from '@/pages/userResponses/store/userResponseStore';
import { selectOriginalUserRole, useAuthStore } from '@/store';
import { useAIGenerateStore } from '@/store/aiGenerateStore';
import { useAIQuestionStatesStore } from '@/store/aiQuestionStatesStore';
import { useExperienceUserStore } from '@/store/experienceUserStore';

const handleStudentPermissions = (exp: any, enabledFeatures: any) => {
  if (!enabledFeatures.allow_peer_review_assessment) return exp;

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const isReviewer = urlParams.get('reviewer');
  const review_for_experience_users = exp.review_for_experience_users ?? [];
  console.log('checkEXP 2 ', isReviewer, review_for_experience_users.length);
  exp.current_user_can_view = false;
  exp.current_user_can_grade = isReviewer && review_for_experience_users.length > 0;
  exp.student_reviewer_mode = isReviewer && review_for_experience_users.length > 0;

  return exp;
};

const handleEditorPermissions = (exp: any, currentUser: any, experiencePermission: any) => {
  const isCommonAssessment = exp?.questionbank?.settings?.dups_editable === false;
  const isDistrictQBAuthor =
    currentUser.role === 'qb_author' && currentUser.org?.org_type === 'district';
  const isEditor = exp?.experience_memberships_editors?.find(
    (em: { memberable_id: number }) =>
      em.memberable_id === currentUser.id && (!isCommonAssessment || isDistrictQBAuthor)
  );

  if (!isEditor) return false;

  return {
    can_grade: experiencePermission.grade,
    can_edit: experiencePermission.update,
    can_view: !experiencePermission.update,
    can_deliver: experiencePermission.deliver,
    can_monitor: experiencePermission.monitor,
    can_view_in_list: true,
  };
};

const handleInvigilatorPermissions = (exp: any, currentUser: any, experiencePermission: any) => {
  // Experience-level invigilator, or a room invigilator who opened the assessment
  // from their room (BE sets `room_inviligator_membership`). Both are treated the
  // same here, so deliver flows through `current_user_can_deliver` like usual.
  const isInvigilator =
    exp.experience_membership_invigilator_ids?.indexOf(currentUser.id) > -1 ||
    !!exp?.room_inviligator_membership?.some((m: any) => m.memberable_id === currentUser.id);
  if (!isInvigilator || !experiencePermission.monitor) return false;

  const canStart = checkIfStartTime(exp);
  return {
    can_view: canStart,
    can_monitor: true,
    can_only_monitor: true,
    can_deliver: canStart && experiencePermission.deliver,
    can_view_in_list: true,
  };
};

const handleAdminPermissions = (exp: any, currentUser: any, experiencePermission: any) => {
  if (currentUser.role !== 'admin' || exp.org_id !== currentUser.org_id) return false;

  return {
    can_grade: experiencePermission.grade,
    can_monitor: experiencePermission.monitor,
    can_deliver: experiencePermission.deliver,
    can_edit: true,
  };
};

const handleQBAuthorPermissions = (exp: any, currentUser: any) => {
  if (currentUser.role !== 'qb_author') return false;

  const allowedQbIds = currentUser.custom_fields?.['allowed_questionbank_ids'];
  // APL-12152 — this copy of the helper (the SHOW-page one) had no district
  // branch, while the list/row copy (experienceManageHelper.ts:61-84) has had
  // one all along. That divergence is the bug: a district qb_author saw the
  // assessment in the list (row copy → can_edit) but lost Edit AND Publish on
  // the show page (this copy → false), because both gate on
  // `current_user_can_edit` (ExperienceHeaderActions.tsx:115-120, :142).
  // The two branches below are ported VERBATIM from the row copy so the two
  // paths can't disagree again; keep them in sync or de-duplicate the helpers.
  // NOTE: v3 (origin/v3-dev) denies this too — an intentional divergence per
  // dev (3 Aug), not a parity fix.
  const isDistrictOrg = currentUser?.org?.org_type === 'district';
  if (
    !isDistrictOrg &&
    (!allowedQbIds || !exp.questionbank || allowedQbIds.indexOf(exp.questionbank.id) === -1)
  )
    return false;

  if (isDistrictOrg && exp.org_id === currentUser.org_id) {
    return {
      can_deliver: true,
      can_edit: true,
      can_grade: true,
    };
  }

  const originalUserRole = selectOriginalUserRole(useAuthStore.getState());
  return {
    can_deliver: originalUserRole === 'superadmin',
    can_edit: true,
  };
};

const handlePamojaSchoolPermissions = (
  exp: any,
  currentUser: any,
  specialUIForPamojaSchool: boolean
) => {
  if (
    !specialUIForPamojaSchool ||
    !['admin', 'teacher'].includes(currentUser.role) ||
    exp.org_id !== currentUser.org_id
  ) {
    return false;
  }

  return {
    can_grade: true,
    can_deliver: false,
    can_monitor: false,
    can_edit: false,
    can_view: true,
  };
};

export const addCanCurrentUserEditForExperiences = (
  experiences: any[],
  enabledFeatures: any,
  currentUser: any
) => {
  // J25-M41: currentUser is null for a frame during login/persona transitions
  // — render read-only (all flags false) instead of throwing.
  if (!currentUser) {
    return experiences.map((exp) =>
      Object.assign(exp, {
        current_user_can_edit: false,
        current_user_can_monitor: false,
        current_user_can_grade: false,
        current_user_can_deliver: false,
        current_user_can_only_monitor: false,
        current_user_can_view: false,
      })
    );
  }

  const experiencePermission = currentUser.permission?.experience ?? {};

  if (currentUser?.role === 'parent') return experiences;
  if (currentUser.role === 'student') {
    return experiences.map((exp) => handleStudentPermissions(exp, enabledFeatures));
  }

  console.log('experiences call form addCanCurrentUserEditForExperiences', experiences);

  return experiences.filter((exp) => {
    const specialUIForPamojaSchool =
      enabledFeatures.special_ui_for_pamoja_school && exp?.custom_fields?.block;

    const permissions = {
      ...handleEditorPermissions(exp, currentUser, experiencePermission),
      ...handleInvigilatorPermissions(exp, currentUser, experiencePermission),
      ...handleAdminPermissions(exp, currentUser, experiencePermission),
      ...handleQBAuthorPermissions(exp, currentUser),
      ...handlePamojaSchoolPermissions(exp, currentUser, specialUIForPamojaSchool),
    };

    Object.assign(exp, {
      current_user_can_edit: permissions.can_edit,
      current_user_can_monitor: permissions.can_monitor,
      current_user_can_grade: permissions.can_grade,
      current_user_can_deliver: permissions.can_deliver,
      current_user_can_only_monitor: permissions.can_only_monitor,
      current_user_can_view: permissions.can_view,
    });

    return permissions.can_view_in_list;
  });
};

export const getExperienceModeOptionsForForm = (opts: { [key: string]: any } = {}) => {
  const options = [];
  const translate = opts?.translate;
  const modesEnabled = {
    online: true,
    online_secure: opts.partnerName != '21k',
    offline: opts.partnerName != '21k' && !opts.isDisableLockdownMode,
    paper: opts.isPaperModeAllowed,
  };
  modesEnabled.online &&
    options.push({
      label: 'Browser',
      value: 'online',
      icon: PlanetLinear,
    });
  modesEnabled.online_secure &&
    options.push({
      label: 'Browser with Security',
      value: 'online_secure',
      icon: Shield03Line,
    });
  modesEnabled.offline &&
    options.push({
      label: 'Lockdown App',
      value: 'offline',
      icon: Lock02Line,
    });
  modesEnabled.paper &&
    options.push({
      label: 'Paper',
      value: 'paper',
      icon: Attachment01Line,
    });
  return options;
};

export const nonIBProgrammeCodes = ['hs', 'ms', 'ps', 'fs'];

export const createUpdateSelectAttrsBeforeSubmit = (
  itemable_type: any,
  existing_items: any,
  modified_ids: any
) => {
  console.log('createUpdateSelectAttrsBeforeSubmit', existing_items, modified_ids);
  // currently only for tag_items - can further generalise
  // both params are arrays
  const items_attributes: any = [];

  let toCreate = null;
  let toRemove = null;
  let toUnarchive = null;
  let existing: any = null;
  let modified = null;
  // create/update
  if (modified_ids) {
    if (typeof modified_ids == 'number') {
      modified = [modified_ids]; // to handle case where single select, modified_ids not an array in this case
    } else {
      modified = modified_ids; // modified_ids is an array
    }
  }
  if (existing_items) {
    existing = _.map(existing_items, 'tag_id');
  }
  toCreate = _.difference(modified, existing);
  toRemove = _.difference(existing, modified);
  toUnarchive = _.intersection(existing, modified);

  console.log('toCreate, toRemove ', toCreate, toRemove);
  if (toCreate) {
    toCreate.map((selectItemId, i) => {
      items_attributes.push({
        itemable_type: itemable_type,
        tag_id: selectItemId, // may need to change when we offer custom syllabus topics per org - based on qbMode
      });
    });
  }
  if (toRemove) {
    toRemove.map((selectItemId, i) => {
      console.log('selectItemId, existing ', selectItemId, existing, existing_items);
      const selectItem = _.find(existing_items, ['tag_id', selectItemId]);
      items_attributes.push({
        id: selectItem.id,
        itemable_type: itemable_type,
        tag_id: selectItemId,
        archived: true,
      });
    });
  }
  if (toUnarchive) {
    toUnarchive.map((selectItemId, i) => {
      console.log('selectItemId, existing ', selectItemId, existing, existing_items);
      const selectItem = _.find(existing_items, ['tag_id', selectItemId]);
      items_attributes.push({
        id: selectItem.id,
        itemable_type: itemable_type,
        tag_id: selectItemId,
        archived: false,
      });
    });
  }

  return items_attributes;
};

export const getRecursiveSegments = (
  data: any,
  parent: any,
  index: any,
  level: any,
  indexPath: any,
  options: any,
  manageUserResponseColumnsData: any
) => {
  console.log(
    'getRecursiveSegments params ==>',
    data,
    parent,
    index,
    level,
    indexPath,
    options.segments,
    manageUserResponseColumnsData
  );
  const { segment_type, segments, withChildren } = options;
  // NOTE: Set the current level index correctly
  indexPath[level] = index;
  // NOTE: Not ordering segments by position here because segments from segmentsSelector are already ordered by position with parent_id too
  const filteredSegments = segments.filter(
    (segment: any) =>
      ((segment_type && segment.segment_type == segment_type) || !segment_type) &&
      segment.parent_id === parseInt(parent.id)
  );
  console.log('CHECK-GROUP-ISSUE-HIGH ==> getRecursiveSegments filteredSegments', segments);

  filteredSegments.forEach((segment: any, j: any) => {
    manageUserResponseColumnsData(data, segment, j, level, indexPath);

    const currentLevel = level + 1;

    // Recursively handle nested_children at any depth
    const processNestedChildren = (children: any[], currentLevel: number, parentIndexPath: any) => {
      const filteredChildren = children.filter(
        (child: any) => segment_type && child.segment_type == segment_type
      );
      console.log('filteredChildren ==>', filteredChildren);
      if (Array.isArray(filteredChildren) && filteredChildren.length > 0) {
        filteredChildren.forEach((child: any, childIdx: any) => {
          const childIndexPath = [...parentIndexPath];
          // Set the child's index at the correct level (currentLevel + 1)
          // because currentLevel represents the parent's level
          childIndexPath[currentLevel + 1] = childIdx;
          console.log('childIndexPath ==>', childIndexPath, currentLevel, childIdx);
          manageUserResponseColumnsData(data, child, childIdx, currentLevel, childIndexPath);
          if (Array.isArray(child.nested_children) && child.nested_children.length > 0) {
            processNestedChildren(child.nested_children, currentLevel + 1, childIndexPath);
          }
        });
      }
    };

    if (
      Array.isArray(segment.nested_children) &&
      segment.nested_children.length > 0 &&
      withChildren
    ) {
      const childIndexPath = [...indexPath];
      // Set the current segment's index (j) at the correct level (level + 1)
      // level is the parent's level, so the current segment is at level + 1
      childIndexPath[level + 1] = j;
      processNestedChildren(segment.nested_children, level + 1, childIndexPath);
    }
  });
};

export const getLabelFromIndexPath = (segmentIndexPath: number[] = []) => {
  const label: string[] = [];
  segmentIndexPath.forEach((index, l) => {
    // segmentIndexPath has indexes, need number so adding 1
    label.push(String(getQuestionNumber(index + 1, l)));
  });
  return label.join('.');
};

export const getQuestionNumber = (num: number, level = 1): string | number => {
  let str = '';
  if (level === 3) {
    // Cost bound: segment_index_path is client-authored; out-of-range values
    // surface as raw digits instead of an unbounded roman repeat() (DoS).
    if (!Number.isInteger(num) || num < 0 || num > 3999) {
      return String(num);
    }
    const roman: Record<string, number> = {
      M: 1000,
      CM: 900,
      D: 500,
      CD: 400,
      C: 100,
      XC: 90,
      L: 50,
      XL: 40,
      x: 10,
      ix: 9,
      v: 5,
      iv: 4,
      i: 1,
    };

    for (const i of Object.keys(roman)) {
      const q = Math.floor(num / roman[i]);
      num -= q * roman[i];
      str += i.repeat(q);
    }

    return '(' + str + ')';
  } else if (level === 2) {
    // Same bound for the letter level: outside a–z renders raw digits, not "()".
    if (!Number.isInteger(num) || num < 1 || num > 26) {
      return String(num);
    }
    str = 'abcdefghijklmnopqrstuvwxyz'.charAt(num - 1);

    return '(' + str + ')';
  } else if (level === 1) {
    return num;
  } else {
    // Level 0 = topics
    // return A, B, C
    // str ='ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(num-1);
    // return str;

    // doing 1,2,3 for now
    return num;
  }
};

export const getNestedSegments = (
  finalSegments: any[],
  parentId: any,
  allDescendants?: any,
  withChildren?: boolean
) => {
  let nestedSegments: any[] = [];

  // If withChildren is true, flatten segments with nested_children
  if (withChildren) {
    const flatArray: any[] = [];

    const flattenSegment = (segment: any) => {
      flatArray.push(segment);

      if (Array.isArray(segment.nested_children) && segment.nested_children.length > 0) {
        segment.nested_children.forEach((child: any) => {
          flattenSegment(child);
        });
      }
    };

    finalSegments.forEach(flattenSegment);
    return flatArray;
  }
  finalSegments.forEach((item) => {
    if (item.parent_id === parentId) {
      nestedSegments.push(item);
      if (allDescendants) {
        nestedSegments = nestedSegments.concat(
          getNestedSegments(finalSegments, item.id, allDescendants)
        );
      }
    }
  });
  return nestedSegments;
};

export const displayPoints = (points: any, truncated = false, cleaned = true) => {
  if (points === null || points === undefined) {
    // console.log("displayPoints - return empty", points)
    return '';
  }
  let pts = parseFloat(points).toFixed(1);
  // Truncate not needed anymore
  // if(truncated && import.meta.env.VITE_MODE === "production"){
  //   // pts = parseFloat(points).toFixed(0)
  //   pts = Math.trunc(parseFloat(points))
  // }
  if (cleaned) {
    const splitPts = pts.split('.');
    // console.log('displayPoints cleaned', cleaned, pts, splitPts);
    if (splitPts.length > 1 && splitPts[1] && parseInt(splitPts[1]) === 0) {
      pts = splitPts[0];
    }
  }
  return pts;
};

export const showConvertedAchievementLevelState = (experience: any) => {
  let showConvertedAchievementLevel = false;
  if (experience?.rubric && experience?.rubric.type_c === 'criteria_with_points') {
    showConvertedAchievementLevel = true;
  }
  return showConvertedAchievementLevel;
};

export const paperModeSettings = {
  with_join_code: false,
  with_session_code: false,
  allow_student_upload_files: true,
  calc_enabled: false,
  graphing_calc_enabled: false,
  spellcheck_enabled: false,
  include_video_call_link: false,
  peer_review_enabled: false,
  // allow_unmapped_students: false,
  allow_guests: false,
};

export const getFirstQuestion = (topics: any[] = [], segments: any[]) => {
  let firstQuestion: any = null;
  const options = { segment_type: 'question', segments: segments };
  const callback = (data: any, segment: any, index: number, level: number) => {
    if (!firstQuestion) {
      if (segment.question_segment_attributes.question_type != 'group') {
        firstQuestion = segment;
      } else {
        getRecursiveSegments({}, segment, index, level + 1, [], options, callback);
      }
    }
  };
  topics &&
    topics.forEach((topic, index) => {
      getRecursiveSegments(
        {},
        topic.segment ? topic.segment : topic,
        index,
        0,
        [],
        options,
        callback
      );
    });

  console.log('first question ==>', firstQuestion);
  return firstQuestion;
};

export const modifyRelationItem = (relationItem: any, initialValues: any) => {
  if (!initialValues) return relationItem;
  // Create a deep copy of initialValues to avoid mutating the original
  const updatedInitialValues = JSON.parse(JSON.stringify(initialValues));

  // Update org_programme_items
  if (relationItem.org_programme_id) {
    const existingOrgProgrammeItem = updatedInitialValues[0].org_programme_items?.[0];
    if (existingOrgProgrammeItem) {
      updatedInitialValues[0].org_programme_id = relationItem.org_programme_id;
    } else {
      updatedInitialValues[0].org_programme_items = [
        {
          org_programme_id: relationItem.org_programme_id,
          itemable_type: 'Experience',
          itemable_id: updatedInitialValues[0].id,
          archived: false,
        },
      ];
    }
  }

  // Update subject_items
  if (relationItem.subject_id) {
    const existingSubjectItem = updatedInitialValues[0].subject_items?.[0];
    if (existingSubjectItem) {
      updatedInitialValues[0].subject_id = relationItem.subject_id;
    } else {
      updatedInitialValues[0].subject_items = [
        {
          subject_id: relationItem.subject_id,
          itemable_type: 'Experience',
          itemable_id: updatedInitialValues[0].id,
          archived: false,
        },
      ];
    }
  }

  // Update grade_items
  if (relationItem.grade_id) {
    const existingGradeItem = updatedInitialValues[0].grade_items?.[0];
    if (existingGradeItem) {
      updatedInitialValues[0].grade_id = relationItem.grade_id;
    } else {
      updatedInitialValues[0].grade_items = [
        {
          grade_id: relationItem.grade_id,
          itemable_type: 'Experience',
          itemable_id: updatedInitialValues[0].id,
          archived: false,
        },
      ];
    }
  }

  if (relationItem.standard_set_tag_data) {
    updatedInitialValues[0].standard_set_tag_data = relationItem.standard_set_tag_data;
  }

  // Update category_tag_id from form
  if ('category_tag_id' in relationItem) {
    updatedInitialValues[0].category_tag_id = relationItem.category_tag_id;
  }

  // Update paper_type_tag_ids from form
  if ('paper_type_tag_ids' in relationItem) {
    updatedInitialValues[0].paper_type_tag_ids = relationItem.paper_type_tag_ids;
  }

  // Update teaching_level_tag_ids from form
  if ('teaching_level_tag_ids' in relationItem) {
    updatedInitialValues[0].teaching_level_tag_ids = relationItem.teaching_level_tag_ids;
  }

  // Update MYP tag_ids from form
  if ('key_concept_tag_ids' in relationItem) {
    updatedInitialValues[0].key_concept_tag_ids = relationItem.key_concept_tag_ids;
  }

  if ('related_concept_tag_ids' in relationItem) {
    updatedInitialValues[0].related_concept_tag_ids = relationItem.related_concept_tag_ids;
  }

  if ('global_context_tag_ids' in relationItem) {
    updatedInitialValues[0].global_context_tag_ids = relationItem.global_context_tag_ids;
  }

  if ('atl_skill_tag_ids' in relationItem) {
    updatedInitialValues[0].atl_skill_tag_ids = relationItem.atl_skill_tag_ids;
  }

  return updatedInitialValues?.[0];
};

export const getSegmentsWithTopicId = (
  topicsData: any[],
  topicId: any,
  allDescendants?: boolean
) => {
  let finalSegments: any[] = [];
  if (topicsData && Array.isArray(topicsData)) {
    finalSegments = topicsData.reduce((acc: any[], topic: any) => {
      return acc.concat(topic.children ?? [], topic.segment ?? []);
    }, []);
  }

  if (topicId) {
    if (allDescendants) {
      finalSegments = getNestedSegments(finalSegments, topicId, allDescendants);
    } else {
      finalSegments = finalSegments.filter((item: any) => item.parent_id === topicId);
    }
  }

  return finalSegments;
};

export const experienceGradingDisabledState = (currentUser: {
  role: string;
  org?: { org_type: string };
}) => {
  let gradingDisabled = false;

  const orgType = currentUser.org?.org_type;
  if (currentUser.role === 'qb_author' && orgType === 'district') {
    gradingDisabled = true;
  }

  return gradingDisabled;
};

type ShowExperienceHelperProps = {
  item: any;
  fromResourceName: any;
  tab?: string;
  currentUser?: any;
  navigate?: any;
  history?: any;
  dispatch?: any;
  event?: any;
  location?: any;
  setLogs?: any;
  appType?: string;
};

export const showExperienceHelper = (props: ShowExperienceHelperProps) => {
  const { setActiveExperienceId } = useExperienceStore.getState();
  const {
    item,
    fromResourceName,
    tab = 'default',
    currentUser = null,
    event = null,
    navigate,
    location,
    appType = 'web',
    // setLogs,
  } = props;
  const isStudent = currentUser?.role === 'student';
  console.log('showExperienceHelper params', item, fromResourceName, tab);

  // let reduxActions = isLibrary ? libraryExperienceReduxActions : experienceReduxActions
  // using same actions now for both so no need
  // const currentUser = useSelector(currentUserSelector())
  // const history = useHistory()
  // const dispatch = useDispatch();
  console.log('showExperienceHelper location', location);
  if (item.experience_type === 'group') {
    return;
  }

  // TO do: this is a problem when calling showExperience from within an experience - setActive should be done on exp mount no?
  setActiveExperienceId(item.id);
  // dispatch(

  //   experienceReduxActions.setActiveExperienceId({ id: item.id }, { success: { showMessage: true } })
  // );

  // Never seed the store with the LIST row here: list wires omit show-only
  // fields (rubric for students/parents, review_for_experience_users, topics)
  // and useExperienceOne's store-wins guard would keep that stub over the show
  // payload. Soft nav must hydrate exactly like a full page load.
  // Added 'tab' parameter to allow navigation to a specific tab; previously, this was only set up for creation.
  const navResult = getExperienceUrl(
    item,
    isStudent,
    appType,
    tab,
    currentUser,
    event,
    fromResourceName
  );
  if (!navResult.shouldNavigate) {
    return;
  }

  if (navResult.openInNewTab) {
    window.open(navResult.url, '_blank');
  } else {
    if (navResult.preventDefault && event) {
      event.preventDefault();
    }

    // Handle state conditions exactly as in the original code
    let state = {};

    if (tab === 'grade') {
      // Grade tab (non-admin) - only fromResourceName
      state = { fromResourceName: fromResourceName };
    } else {
      // Default tab - both fromResourceName and fromUrl
      state = {
        fromResourceName: fromResourceName,
        fromUrl: location ? location.pathname + location.search : null,
      };
    }

    navigate(navResult.url, { state });
  }
  // using same url now for both so no need
  // history.push(isLibrary ? `/e/all-tests/${item.id}` : `/experiences/${item.id}`)
  // if (tab === 'monitor') {
  //   window.open(`/experiences/${item.uid}/monitor`, '_blank');
  // } else if (tab === 'grade') {
  //   if (
  //     currentUser.role === 'qb_author' ||
  //     currentUser.role === 'superadmin' ||
  //     currentUser.role === 'support'
  //   ) {
  //     window.open(`/experiences/${item.uid}/grade`, '_blank');
  //   } else {
  //     push(`/experiences/${item.uid}/grade`, {
  //       state: { fromResourceName: fromResourceName },
  //     });
  //   }
  // } else if (currentUser.role === 'superadmin' || currentUser.role === 'support') {
  //   // default to new tab
  //   window.open(`/experiences/${item.uid}`, '_blank');
  // } else if (event && (event.ctrlKey || event.metaKey)) {
  //   //  to support Ctrl + click for new tab
  //   if (currentUser.role === 'student') {
  //     //  NOT for student - we always want to open in same tab
  //     event.preventDefault();
  //   } else {
  //     window.open(`/experiences/${item.uid}/${fromResourceName}`);
  //   }
  // } else {
  //   // default behaviour with just click - same tab
  //   push(`/experiences/${item.uid}`, {
  //     state: {
  //       fromResourceName: fromResourceName,
  //       fromUrl: location ? location.pathname + location.search : null,
  //     },
  //   });
  // }
};

type UnMountExperienceProps = {
  item: any;
  appType: any;
  currentUser: any;
  invalidate?: ReturnType<typeof useInvalidate>;
};

export const unMountExperience = (props: UnMountExperienceProps) => {
  const { item, appType, currentUser, invalidate } = props;
  const { setActiveSegment } = useSegmentStore.getState();
  const { setActiveTopicId, setActiveTopic } = useTopicStore.getState();
  const {
    setActiveExperienceId,
    setShowPreviewSuccess,
    setShowPdfCompare,
    setExperienceViewMode,
    setExperience,
  } = useExperienceStore.getState();
  const {
    setLocalUserInfo,
    setLocalExperienceInfo,
    setLocalUserResponses: setRealtimeLocalUserResponses,
    setHighlightTextEnabled,
    setPendingUserInfo,
  } = useRealtimeExperienceStore.getState();
  const { setAnnotations } = useExperienceUserStore.getState();
  const { setLocalUserResponses: setDBLocalUserResponses } = useUserResponseStore.getState();

  // Clear refine.dev resources if invalidate function is provided
  if (invalidate) {
    invalidate({
      resource: 'experiences',
      invalidates: ['all'],
    });
    invalidate({
      resource: 'experience_topics_and_segments',
      invalidates: ['all'],
    });
    invalidate({
      resource: 'experience_users',
      invalidates: ['all'],
    });
    invalidate({
      resource: 'user_responses',
      invalidates: ['all'],
    });
    invalidate({
      resource: 'segments',
      invalidates: ['all'],
    });
  }

  if (appType === 'mac' && item?.settings?.mode === 'offline' && currentUser.role === 'student') {
    window.removeEventListener('AssessPrepFocusLost', function handleFocusLost() {
      // Event handler removed, no action needed
    });
  }

  // Close AI panel and reset AI state
  const { triggerReset, setSidebarVisible } = useAIGenerateStore.getState();
  const { resetQuestionStates } = useAIQuestionStatesStore.getState();
  triggerReset();
  // R2-G32: triggerReset is a no-op once the panel unmounted (its reset
  // callback unregisters with it) — hard-reset so generated-but-unaccepted
  // results never survive into a reopen of the same assessment.
  useAIGenerateStore.getState().resetAIGenerateStore();
  resetQuestionStates();
  setSidebarVisible(false);

  setActiveSegment(null);
  setActiveTopicId(null);
  setActiveTopic(null);
  setActiveExperienceId(null);
  setExperience(null);
  setExperienceViewMode(null);

  setShowPreviewSuccess(false);
  setShowPdfCompare(false);
  // R2-G56 rider: the highlighter tool disarms when the take SURFACE tears
  // down (was per-hook-unmount inside useTextHighlighter, which switched the
  // tool off whenever any single stem/passage unmounted mid-exam).
  setHighlightTextEnabled(false);
  setLocalUserInfo({}, { merge: false });
  // A new exam must not inherit another exam's pending replay values.
  setPendingUserInfo({}, { merge: false });
  setLocalExperienceInfo({}, { merge: false });
  setRealtimeLocalUserResponses({}, { merge: false });
  setDBLocalUserResponses([], { merge: false });
  setAnnotations([]);

  //TODO: v3 set graphingCalcData to null
  // setToLS('graphingCalcData', null);
  // dispatch action to disconnect firebase realtime db
};

const handleMypAchievementLevel = (item: any, itemType: string) => {
  if (itemType === 'user_response') {
    return item.marked ? 'blue' : '';
  }

  // eu case only now
  const urSCAs = item.submission_criterium_associations_attributes;
  if (!urSCAs) return '';

  // not using item.max_points or item.points - not accurate - see APL-2971
  const totalPoints = urSCAs.length * 8;
  let currentPoints = 0;
  let scaPointsNull = false;

  urSCAs.forEach((sca: any) => {
    if (sca.points) {
      currentPoints += parseFloat(sca.points);
    } else if (!sca?.custom_fields?.is_na) {
      scaPointsNull = true;
    }
  });

  if (scaPointsNull) {
    return 'gray'; // means overall ungraded
  }

  return getColorByPercentage(currentPoints, totalPoints);
};

const handleCommentsOnly = (item: any, itemType: string) => {
  const hasComments = !isStringEmpty(item.comments);
  const hasMediaComments = item.custom_fields?.media_comments?.attachments?.length > 0;

  const decidingFactor =
    itemType === 'experience_user' ? hasComments || hasMediaComments : item.marked;

  return decidingFactor ? 'blue' : 'gray';
};

const handlePointsBasedRubric = (item: any, itemType: string) => {
  const currentPoints = item.points;
  const totalPoints =
    itemType === 'experience_user' ? item?.custom_fields?.experience_points : item?.max_points;

  return getColorByPercentage(currentPoints, totalPoints);
};

const getColorByPercentage = (currentPoints: number, totalPoints: number) => {
  const percent = calculatePercentage(currentPoints, totalPoints);

  if (percent <= 25) return 'red';
  if (percent <= 50) return 'orange';
  if (percent <= 75) return 'yellow';
  if (percent <= 100) return 'green';
  // if (percent > 100) return 'red-dark';

  return '';
};

export const generateClassName = (item: any, rubricType: any, itemType = 'user_response') => {
  if (!item) return '';

  // console.log('generateClassName => userresponse', item);
  if (!item.attempted) {
    return 'not-attempted';
  }

  if (!item.marked && itemType === 'user_response') {
    return 'gray';
  }

  if (rubricType === 'myp_achievement_level') {
    return handleMypAchievementLevel(item, itemType);
  }

  if (rubricType === 'comments_only' || rubricType === 'custom') {
    return handleCommentsOnly(item, itemType);
  }

  return handlePointsBasedRubric(item, itemType);
};

export const setURLWithExperienceUserId = (eu_id: number) => {
  const url = window.location.pathname;
  const cleanUrl = url.replace(/\/\d+$/, '');
  window.history.replaceState(null, '', `${cleanUrl}/${eu_id}`);
};

export const getExperienceUserIdFromURL = (): number | null => {
  const url = window.location.pathname;
  const match = url.match(/\/grade\/(\d+)$/);
  return match ? parseInt(match[1]) : null;
};

export const removeExperienceUserIdFromURL = (): void => {
  const url = window.location.pathname;
  const cleanUrl = url.replace(/\/grade\/\d+$/, '/grade');
  window.history.replaceState(null, '', cleanUrl);
};

export const highLightActiveCell = (selectedCell: Element) => {
  // if (selectedCell) {
  //   selectedCell.scrollIntoView({ inline: 'center', block: 'center' });
  //   selectedCell.classList.add('selected-cell');
  //   const tdElement = selectedCell.closest('td');
  //   if (tdElement) {
  //     tdElement.style.outline = 'none';
  //   }
  //   selectedCell.closest('td')?.focus();
  // }
};

export const removeHighLightActiveCellClass = () => {
  const cells = document.querySelectorAll('.selected-cell');
  cells &&
    cells.forEach((cell) => {
      cell.classList.remove('selected-cell');
    });
};

/**
 * Extracts AI grading status from an experience user's custom fields
 * @param experienceUser - The experience user object containing custom fields
 * @returns The AI grading status object or empty object if not found
 * Used to safely access nested AI grading status data without causing errors
 */
export const getAIGradingStatus = (item?: any, itemType = 'experience_user') => {
  if (itemType === 'experience_user') {
    return item?.custom_fields?.ai_grading_status ?? {};
  }

  return {};
};

/**
 * Checks if a specific submission is currently being graded by AI
 * @param experienceUser - The experience user object to check
 * @returns Boolean indicating if AI grading is in progress for this submission
 * Used to show loading states, disable actions, and control UI behavior during AI grading
 */
export const isGradingWithAIInProgress = (item?: any, itemType = 'experience_user') => {
  const aiGradingStatus = getAIGradingStatus(item, itemType);
  // Check if status is 'started' or 'in_progress' to determine if AI grading is active
  return ['started', 'in_progress'].includes(aiGradingStatus?.status);
};

/** The active run covering this EU's session, off the tab-wide runs store
 *  (APL-12308 — the custom_fields run registry is gone). */
const activeAIGradingRunForEU = (eu: any) =>
  activeAIGradingRunForSession(
    useAIGradingRunsStore.getState().activeRuns,
    eu?.experience_session?.id ?? eu?.experience_session_id
  );

export const isGradingWithAIInProgressForEU = (eu: any, experience: any) => {
  const isExperienceUserSeeded = eu?.custom_fields?.is_seed ?? false;
  const isExperienceUserPublished = eu?.marked ?? false;
  const noQuestionsAttempted = eu?.attempted_responses_count === 0;
  if (isExperienceUserSeeded || isExperienceUserPublished || noQuestionsAttempted) return false;

  // A run stamps every targeted EU in_progress at start; the EU's own terminal
  // status clears its row as grades land.
  if (getAIGradingStatus(eu, 'experience_user')?.status) {
    return isGradingWithAIInProgress(eu, 'experience_user');
  }
  // Unstamped row + covering active run: the row was fetched BEFORE the run
  // started (stamps arrive with the start-triggered refetch) — the live runs
  // store is the only signal, so the row loads.
  const coveringRun = activeAIGradingRunForEU(eu);
  if (!coveringRun) return false;
  // A hand-picked run grades only its own submissions; every other row in the
  // session stays idle. An older run carries neither field and stays
  // session-wide.
  if (coveringRun.hand_picked_roster === true) {
    return (coveringRun.experience_user_ids ?? []).some(
      (id: string | number) => String(id) === String(eu?.id)
    );
  }
  return true;
};

// Function that checks if grading is in progress for ungraded user responses
export const isGradingWithAIInProgressForUR = (
  eu: any,
  experience: any,
  isUserResponseMarked: boolean,
  segmentId?: string | number | null
) => {
  const inProgressForEU = isGradingWithAIInProgressForEU(eu, experience);
  // The covering active run carries the only-ungraded choice; per-EU stamps do
  // not, so a run started with the panel closed loads marked cells too.
  const coveringRun = activeAIGradingRunForEU(eu);
  const isGradingUngradedResponsesWithAI = !!coveringRun?.only_ungraded;

  // Question-scoped runs stamp `segment_ids` (always an array) on the EU
  // status — loading shows only on those columns. An unstamped row (its
  // in-progress signal is the runs store) reads the covering run's question
  // scope instead. Absent = full run, whole row.
  if (segmentId != null && inProgressForEU) {
    const euStamp = getAIGradingStatus(eu, 'experience_user');
    const runSegmentIds = euStamp?.status ? euStamp?.segment_ids : coveringRun?.segment_ids;
    if (
      Array.isArray(runSegmentIds) &&
      runSegmentIds.length > 0 &&
      !runSegmentIds.some((id: any) => String(id) === String(segmentId))
    ) {
      return false;
    }
  }

  return inProgressForEU && !(isGradingUngradedResponsesWithAI && isUserResponseMarked);
};

/**
 * Analyze all experience users to determine:
 * 1. If any individual submissions are being graded by AI
 * 2. If all submissions have been published/marked
 * @param experienceUsers - Array of experience user objects to analyze
 * @returns Object containing AI grading status and publication status
 * Used for UI state management and determining when operations are complete
 */
export const analyzeExperienceUsersStatus = (experienceUsers: any[]) => {
  const { isGradingWithAIInProgressForSomeEUs, isAllSubmissionsArePublished } =
    experienceUsers.reduce(
      (acc: any, eu: any) => {
        // Check if any individual submission is currently being graded by AI using shared utility
        if (!acc.isGradingWithAIInProgressForSomeEUs && isGradingWithAIInProgress(eu)) {
          acc.isGradingWithAIInProgressForSomeEUs = true;
        }
        // Check if all submissions have been marked/published
        if (acc.isAllSubmissionsArePublished && !eu.marked) {
          acc.isAllSubmissionsArePublished = false;
        }
        return acc;
      },
      {
        // Initial state: assume no AI grading in progress and all submissions published
        isGradingWithAIInProgressForSomeEUs: false,
        isAllSubmissionsArePublished: true,
      }
    );

  return { isGradingWithAIInProgressForSomeEUs, isAllSubmissionsArePublished };
};

export const enableFocusLossTrackingForSecureMode = (experience: any) => {
  if (experience?.settings?.mode === 'online_secure') {
    experience.settings.focus_loss_tracking = true;
  }
  return experience;
};
export const canStudentReview = (experience: any, enabledFeatures: any) => {
  const experienceSettings = experience?.settings ?? {};
  const experienceUser = experience.experience_user;
  const experienceUserStatus = experienceUser?.status;
  const isSubmitted = experienceUserStatus === 'taken';
  const isPeerReviewEnabled = experienceSettings.peer_review_enabled || false;

  const isReviewer =
    enabledFeatures.allow_peer_review_assessment && isPeerReviewEnabled && isSubmitted;

  return isReviewer;
};

export const getMBTaskUrl = (org: any, experience: any) => {
  let subdomain = org.custom_fields.subdomain;
  if (import.meta.env.VITE_MODE === 'demo') {
    subdomain = 'demo';
  }
  if (import.meta.env.VITE_MODE === 'staging') {
    subdomain = 'faria-partners.devel';
  }
  const domainSuffix = import.meta.env.VITE_REGION === 'china' ? 'cn' : 'com';
  const mbClassId = experience.custom_fields.mb_class_id;
  const mbTaskId = experience.custom_fields.mb_task_id;

  return `https://${subdomain}.managebac.${domainSuffix}/teacher/classes/${mbClassId}/core_tasks/${mbTaskId}`;
};

export const calculatePointsForSorting = (obj: any) => {
  const hasSCA =
    obj.submission_criterium_associations_attributes &&
    obj.submission_criterium_associations_attributes.length > 0;
  if (hasSCA) {
    const allNull = obj.submission_criterium_associations_attributes.every(
      (sca: any) => sca.points === null
    );
    if (allNull) {
      return -1;
    }
    return obj.submission_criterium_associations_attributes.reduce((total: any, sca: any) => {
      const points = sca.points !== null && sca.points !== undefined ? parseInt(sca.points) : 0;
      return total + points;
    }, 0);
  }
  // returning -1 if obj.points are null to first sort ungraded, then 0 and then points greater than 0
  return obj.points === null ? -1 : obj.points;
};

export const getExperienceDurationForStudent = (userInfo: any, experienceSettings: any) => {
  return userInfo.duration_after_reset
    ? userInfo.duration_after_reset
    : experienceSettings.duration;
};

export const updateSegmentTypeCount = (data: any, experience: any) => {
  const newExperience = {
    ...experience,
    custom_fields: {
      ...experience.custom_fields,
      segment_types_count: data.exp_segment_types_count,
    },
  };
  return newExperience;
};

export const isDistrictQBAuthorWithMonitorFeature = (enabledFeatures: any, currentUser: any) => {
  const enableMonitorForDistrictCommonAssessment =
    enabledFeatures.enable_monitor_for_district_common_assessment;
  const currentUserOrg = currentUser.org ?? {};
  const isDistrictQBAuthor =
    currentUser?.role === 'qb_author' && currentUserOrg.org_type === 'district';

  return enableMonitorForDistrictCommonAssessment && isDistrictQBAuthor;
};

/**
 * Checks if sessions should be shown for an experience
 * Sessions are shown if:
 * 1. The global feature flag `enable_experience_sessions` is enabled, OR
 * 2. The question bank setting `should_show_session` is true
 * @param enabledFeatures - The enabled features object
 * @param experience - The experience object (should have questionbank with settings)
 * @returns boolean indicating if sessions should be shown
 */
export const shouldShowSessions = (enabledFeatures: any, experience: any): boolean => {
  // Check question bank setting
  const questionbank = experience?.questionbank;
  const questionbankSettings = questionbank?.settings;
  if (
    questionbankSettings?.should_show_session === true &&
    enabledFeatures?.separate_common_assessment_tab &&
    questionbankSettings?.dups_editable === false
  ) {
    return true;
  }

  return false;
};

/**
 * Checks if any session has paper mode
 * @param sessions - Array of session objects
 * @returns boolean indicating if any session has paper mode
 */
export const hasAnyPaperModeSession = (sessions: any[]): boolean => {
  if (!sessions || !Array.isArray(sessions)) return false;
  return sessions.some((session: any) => session?.settings?.mode === 'paper');
};

export const applyShuffledOrder = (topicsAndSegmentsData: any, userInfo: any) => {
  const studentTopicsSegmentsOrder = userInfo?.topics_segments_order;

  // Return early if data is invalid or not an array
  if (!topicsAndSegmentsData || !Array.isArray(topicsAndSegmentsData)) {
    return topicsAndSegmentsData;
  }

  // If student already has a shuffled order saved, reorder the data based on saved IDs
  // This ensures consistent ordering across sessions for the same student
  if (studentTopicsSegmentsOrder?.topics_order && studentTopicsSegmentsOrder?.segments_order) {
    const { topics_order, segments_order } = studentTopicsSegmentsOrder;

    console.log('studentTopicsSegmentsOrder ==>', studentTopicsSegmentsOrder);

    // Create a map for quick lookup of topics/segments by ID
    const dataMap = new Map(
      topicsAndSegmentsData.map((item) => [item.segment?.id || item.id, item])
    );

    // Track which topic IDs were processed from saved order
    const processedTopicIds = new Set<number>();

    // Reorder topics/segments based on topics_order
    const reorderedData = topics_order
      .map((topicId: number) => {
        const item = dataMap.get(topicId);
        if (!item) return null;

        // Mark this topic as processed
        processedTopicIds.add(topicId);

        // Get the children order for this parent
        const childrenOrder = segments_order[topicId.toString()];

        // If no children order or no children, return item as-is
        if (!childrenOrder || !Array.isArray(childrenOrder) || !item.children?.length) {
          return item;
        }

        // Create children map and reorder in one pass
        const childrenMap = new Map(item.children.map((child: any) => [child.id, child]));

        // Track which children were processed from saved order
        const processedChildIds = new Set<number>();

        // Reorder children based on saved IDs and remove any undefined/null entries
        // filter(Boolean) is crucial here because:
        // 1. Map.get() returns undefined if the childId doesn't exist in childrenMap
        // 2. Questions/segments might have been deleted from the experience after the order was saved
        // 3. Data inconsistencies between saved order and current data structure
        // 4. Without filtering, undefined values would break rendering and cause runtime errors
        const reorderedChildren = childrenOrder
          .map((childId: number) => {
            const child = childrenMap.get(childId);
            if (child) {
              processedChildIds.add(childId);
            }
            return child;
          })
          .filter(Boolean); // Remove all falsy values (undefined, null) to ensure data integrity

        // Find and append any new children that weren't in the saved order
        const newChildren = item.children.filter((child: any) => !processedChildIds.has(child.id));

        return {
          ...item,
          children: [...reorderedChildren, ...newChildren],
        };
      })
      .filter(Boolean); // Remove null entries from topics where items were not found in dataMap

    // Find and append any new topics that weren't in the saved order
    const newTopics = topicsAndSegmentsData.filter((item: any) => {
      const topicId = item.segment?.id || item.id;
      return !processedTopicIds.has(topicId);
    });

    return [...reorderedData, ...newTopics];
  }

  // If no saved order, return original data
  return topicsAndSegmentsData;
};

export const getShuffledOrder = (
  topicsAndSegmentsData: any,
  type: 'questions' | 'sections_questions'
) => {
  // Return early if data is invalid or not an array
  if (!topicsAndSegmentsData || !Array.isArray(topicsAndSegmentsData)) {
    return null;
  }

  let shuffledData = topicsAndSegmentsData;

  // For 'sections_questions' type, shuffle the segments/sections at the top level
  // For 'questions' type, keep original segment order
  if (type === 'sections_questions') {
    shuffledData = _.shuffle(topicsAndSegmentsData);
  }

  // Shuffle children (questions) within each segment for both 'questions' and 'sections_questions' types
  if (type === 'questions' || type === 'sections_questions') {
    shuffledData = shuffledData.map((item) => ({
      ...item,
      children: item.children ? _.shuffle(item.children) : item.children,
    }));
  }

  // Extract IDs from shuffled data
  const topics_order: number[] = [];
  const segments_order: { [key: string]: number[] } = {};

  shuffledData.forEach((item: any) => {
    const topicId = item.segment?.id || item.id;
    topics_order.push(topicId);

    // Save children order for this topic/segment
    if (item.children && Array.isArray(item.children)) {
      segments_order[topicId.toString()] = item.children.map((child: any) => child.id);
    }
  });

  return {
    topics_order,
    segments_order,
    shuffled_type: type, // Store type for future comparison
  };
};

/**
 * Array of rubric types that are allowed for AI grading.
 * This array can be used to check if a specific rubric type supports AI grading.
 */
export const ALLOWED_RUBRIC_TYPES_FOR_AI_GRADING: RubricTypes[] = [
  RubricTypes.Points,
  RubricTypes.CommentsOnly,
  RubricTypes.CriteriaWithPoints,
  RubricTypes.AchievementLevel,
  // Custom joined when the custom-overall AI grading path shipped.
  RubricTypes.Custom,
];

/**
 * Helper function to check if a rubric type is allowed for AI grading.
 * @param rubricType - The rubric type to check (can be enum value or string)
 * @returns true if the rubric type is allowed for AI grading, false otherwise
 */
export const isRubricTypeAllowedForAIGrading = (rubricType: string | RubricTypes): boolean => {
  return ALLOWED_RUBRIC_TYPES_FOR_AI_GRADING.includes(rubricType as RubricTypes);
};

export enum ALL_QUESTION_TYPES {
  LongAnswer = 'cke_subjective',
  MCQSingle = 'mcq_single',
  MCQMultiple = 'mcq_multiple',
  TrueFalse = 'true_false',
  FillText = 'cke_fill_text',
  FillDropdown = 'cke_fill_dropdown',
  Match = 'rbd_inline_matching',
  Sort = 'rbd_inline_sorting',
  Classify = 'rbd_inline_classify',
  Table = 'rds_table',
  Drawing = 'fabric_drawing',
  GeogebraGraph = 'ggb_graph',
  DesmosGraph = 'desmos_graph',
  AudioInput = 'audio_input',
  VideoInput = 'video_input',
  FileUpload = 'file_upload',
  LabelDrag = 'fabric_dnd_label',
  LabelFill = 'fabric_fill_label',
  Hotspot = 'fabric_hotspot',
  MiniQuiz = 'mini_quiz',
  Connect = 'connect',
  FlashCard = 'flash_card',
}

//allowed question types for grading with AI backend (for grade all with ai button)
export const QUESTION_TYPES_ALLOWED_FOR_GRADING_WITH_AI_BACKEND = [
  ALL_QUESTION_TYPES.LongAnswer,
  ALL_QUESTION_TYPES.MCQSingle,
  ALL_QUESTION_TYPES.MCQMultiple,
  ALL_QUESTION_TYPES.TrueFalse,
  ALL_QUESTION_TYPES.FillText,
  ALL_QUESTION_TYPES.FillDropdown,
  ALL_QUESTION_TYPES.Match,
  ALL_QUESTION_TYPES.Sort,
  ALL_QUESTION_TYPES.Classify,
];

export const getEdlinkIntegration = (currentUser: any) => {
  const orgIntegrations = currentUser?.org?.org_integrations ?? [];
  const edlinkIntegration = orgIntegrations.filter(
    (oi: any) => oi.integration?.code === 'edlink' && oi.archived === false
  )?.[0];

  return {
    edlinkIntegration,
    edlinkIntegrationId: edlinkIntegration?.id ?? null,
    edlinkIntegrationSource: edlinkIntegration?.source ?? '',
    edlinkIntegrationCode: edlinkIntegration?.integration?.code ?? '',
  };
};

export const shouldShowSyncButton = ({
  experience,
  enabledFeatures,
  edlinkIntegration,
  isAdminOrTeacher,
  dupFromId,
  activeExperienceGradingDisabled,
}: {
  experience: any;
  enabledFeatures: any;
  edlinkIntegration: any;
  isAdminOrTeacher?: any;
  dupFromId?: any;
  activeExperienceGradingDisabled?: boolean;
}) => {
  return !!(
    experience?.has_active_link &&
    !activeExperienceGradingDisabled &&
    enabledFeatures?.show_sync_button_in_actions &&
    edlinkIntegration &&
    (isAdminOrTeacher === undefined || Boolean(isAdminOrTeacher)) &&
    !dupFromId
  );
};

/**
 * True when the experience's parent QB has `visible_for_role: admin_collaborators`.
 * In this mode the BE auto-scopes a teacher's experiences and sessions to ones
 * they're a member of (creator / collaborator / invigilator), so any FE filter
 * that further narrows by `by_owner_id` would over-restrict and hide sessions
 * the teacher is a collaborator on.
 */
export const isAdminCollaboratorsModeInQB = (experience: any): boolean =>
  experience?.questionbank?.settings?.visible_for_role === 'admin_collaborators';

// A level pick or points on the sca — overall-graded rubrics mark EU scas,
// never responses.
export const hasCriterionGrade = (sca: any): boolean => {
  const rawLevelId = sca?.level_id ?? sca?.points;
  return rawLevelId != null && String(rawLevelId).trim() !== '';
};

// For a custom rubric, the sca stores the picked level as `level_id` (1-indexed)
// + `custom_fields.level_label`; label/descriptor live on criterion `levels_json`.
export const getCustomCriterionLevelDisplay = (
  experienceUser: any,
  criteriumAssociation: any,
  criterion?: any
): {
  label: string;
  desc: string | null;
  graded: boolean;
  levelOrdinal: number | null;
  levelCount: number | null;
  points: number | string | null;
  maxPoints: number | string | null;
} => {
  const NOT_GRADED = {
    label: '—',
    desc: null,
    graded: false,
    levelOrdinal: null,
    levelCount: null,
    points: null,
    maxPoints: null,
  };

  const scas = experienceUser?.submission_criterium_associations_attributes ?? [];
  const sca = scas.find((s: any) => s?.criterium_id === criteriumAssociation?.criterium_id);

  const rawLevelId = sca?.level_id ?? sca?.points;
  if (!hasCriterionGrade(sca)) {
    return NOT_GRADED;
  }

  const levels = Array.isArray(criterion?.levels_json) ? criterion.levels_json : [];
  const matchedLevel = levels.find((lvl: any) => String(lvl?.id) === String(rawLevelId));
  const matchedIndex = levels.findIndex((lvl: any) => String(lvl?.id) === String(rawLevelId));
  const levelCount = levels.length > 0 ? levels.length : null;
  // Fall back to the raw numeric level id when not found in levels_json (legacy data).
  let levelOrdinal: number | null = null;
  if (matchedIndex >= 0) {
    levelOrdinal = matchedIndex + 1;
  } else if (!Number.isNaN(Number(rawLevelId))) {
    levelOrdinal = Number(rawLevelId);
  }

  const savedLabel = sca?.custom_fields?.level_label;
  const levelLabel = matchedLevel?.label ?? matchedLevel?.level_label;

  let label: string;
  if (savedLabel != null && String(savedLabel).trim() !== '') {
    label = String(savedLabel);
  } else if (levelLabel != null && String(levelLabel).trim() !== '') {
    label = String(levelLabel);
  } else {
    label = `L${rawLevelId}`;
  }

  const desc =
    matchedLevel?.desc != null && String(matchedLevel.desc).trim() !== ''
      ? String(matchedLevel.desc)
      : null;

  return {
    label,
    desc,
    graded: true,
    levelOrdinal,
    levelCount,
    points: sca?.points ?? null,
    maxPoints: sca?.custom_fields?.max_points ?? null,
  };
};

// R2-G01 (shared law): custom+use_scoring totals derive from the SCAs — the
// criteria are the rubric's whole scoring surface; experience.points/eu.points
// can carry stale SEGMENT points (library-duplicated questions keep theirs).
export const customScaTotals = (scas: any[]): { points: number; max: number } | null =>
  Array.isArray(scas) && scas.length > 0
    ? scas.reduce(
        (acc: { points: number; max: number }, sca: any) => ({
          points: acc.points + (Number(sca?.points) || 0),
          max: acc.max + (Number(sca?.custom_fields?.max_points) || 0),
        }),
        { points: 0, max: 0 }
      )
    : null;

// Experience-level possible total (table header — no EU in scope): highest
// configured level_points per associated criterion, summed.
export const customRubricMaxPoints = (expCas: any[], criteria: any[]): number =>
  (expCas ?? []).reduce((sum: number, ca: any) => {
    const criterion = (criteria ?? []).find((c: any) => String(c?.id) === String(ca?.criterium_id));
    const levels = Array.isArray(criterion?.levels_json) ? criterion.levels_json : [];
    const max = levels.reduce((highest: number, level: any) => {
      const pts = parseFloat(String(level?.level_points ?? ''));
      return Number.isFinite(pts) && pts > highest ? pts : highest;
    }, 0);
    return sum + max;
  }, 0);

// Maps an achieved level to a Tag preset colour proportional to scale size
// (low→high: red → gold → green → cyan); undefined when the ordinal is unresolved.
export const getCustomLevelTagColor = (
  levelOrdinal: number | null,
  levelCount: number | null
): string | undefined => {
  if (levelOrdinal == null || levelOrdinal < 1) {
    return undefined;
  }
  const count = levelCount != null && levelCount >= 1 ? levelCount : 4;
  const clamped = Math.min(Math.max(levelOrdinal, 1), count);
  if (count <= 1) {
    return 'green';
  }
  const fraction = (clamped - 1) / (count - 1);
  if (fraction <= 0) return 'red';
  if (fraction >= 1) return 'cyan';
  if (fraction < 0.5) return 'gold';
  return 'green';
};
